import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { formatMoney } from '../../../utils/currency';
import { activityStatus, count, formatDateTime, formatDay } from './reportsFormat';
import useReport from './useReport';
import ReportState from './ReportState';

const STATUS_COLOR = { ok: 'success', slow: 'warning', dormant: 'error', never: 'default', inactive: 'default' };
const EMPTY_PARAMS = {};

const FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'ok', label: 'Activas' },
  { value: 'slow', label: 'Actividad baja' },
  { value: 'dormant', label: 'Dormidas' },
  { value: 'never', label: 'Sin actividad' },
  { value: 'inactive', label: 'Dadas de baja' },
];

const SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  lastActivity: (a, b) => (a.lastActivityAt || '').localeCompare(b.lastActivityAt || ''),
  sales: (a, b) => Number(a.monthToDate.salesTotalUyu) - Number(b.monthToDate.salesTotalUyu),
  size: (a, b) => Number(a.dbSizeMb || 0) - Number(b.dbSizeMb || 0),
  staff: (a, b) => a.staffTotal - b.staffTotal,
  clients: (a, b) => a.clientsTotal - b.clientsTotal,
};

export default function CompaniesTab({ onFreshness }) {
  const report = useReport((p) => CoreReportsService.companies(p), EMPTY_PARAMS);
  const data = report.data;
  const freshness = data?.freshness || null;
  useEffect(() => { onFreshness?.(freshness); }, [freshness, onFreshness]);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = (data?.companies || [])
      .map((c) => ({ ...c, status: activityStatus(c.daysSinceActivity, c.isActive) }))
      .filter((c) => (filter === 'all' || c.status.key === filter) && (!needle || `${c.name} ${c.domain}`.toLowerCase().includes(needle)));
    const compare = SORTS[sort.key];
    list.sort((a, b) => (sort.dir === 'asc' ? compare(a, b) : compare(b, a)));
    return list;
  }, [data, query, filter, sort]);

  const header = (key, label, align = 'left') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'}
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <ReportState report={report}>
      {data && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small" label="Buscar empresa" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 260 }}
            />
            <TextField size="small" select label="Estado" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 190 }}>
              {FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
            </TextField>
            <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
              {count(rows.length)} de {count(data.companies.length)} empresas
              {data.monthStart && ` · ventas del mes desde el ${formatDay(data.monthStart)}`}
            </Typography>
          </Stack>

          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {header('name', 'Empresa')}
                  <TableCell>Estado</TableCell>
                  {header('lastActivity', 'Última actividad')}
                  {header('sales', 'Ventas del mes (pesos)', 'right')}
                  <TableCell align="right">Ventas del mes (dólares)</TableCell>
                  <TableCell>Funciones opcionales</TableCell>
                  {header('staff', 'Staff', 'right')}
                  {header('clients', 'Clientes', 'right')}
                  {header('size', 'BD (MB)', 'right')}
                  <TableCell align="right">Shard</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>Ninguna empresa coincide con el filtro.</TableCell></TableRow>
                )}
                {rows.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.domain}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[c.status.key]} variant={c.status.key === 'ok' ? 'filled' : 'outlined'} label={c.status.label} /></TableCell>
                    <TableCell>{c.lastActivityAt ? formatDateTime(c.lastActivityAt) : 'Nunca'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatMoney(c.monthToDate.salesTotalUyu, 'UYU', { decimals: 0 })}
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{count(c.monthToDate.salesCountUyu)} ventas</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {Number(c.monthToDate.salesCountUsd) > 0 ? (
                        <>
                          {formatMoney(c.monthToDate.salesTotalUsd, 'USD', { decimals: 0 })}
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{count(c.monthToDate.salesCountUsd)} ventas</Typography>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {c.plugins.length ? c.plugins.map((p) => <Chip key={p} size="small" variant="outlined" label={p} />) : <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ninguna</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{count(c.staffTotal)}</TableCell>
                    <TableCell align="right">{count(c.clientsTotal)}</TableCell>
                    <TableCell align="right">{c.dbSizeMb === null || c.dbSizeMb === undefined ? '-' : Number(c.dbSizeMb).toLocaleString('es-UY', { maximumFractionDigits: 1 })}</TableCell>
                    <TableCell align="right">{c.shardId}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </ReportState>
  );
}
